import {AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {APP_CONFIG_TOKEN, AppConfig} from '../../app.config';
import {Notification, NOTIFICATION_METADATA, Severity, STATUSES} from 'smag-commons/dto/msg';
import {BehaviorSubject, combineLatest, merge, Observable, of, ReplaySubject, Subject} from 'rxjs';
import {catchError, filter, map, shareReplay, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';
import {NotificationsService, ViewNotification} from '../../shared/notifications.service';
import {Router} from '@angular/router';
import {I18NService} from '../../i18n/i18n.service';
import {HierarchyEntity, HierarchyService} from '../../shared/hierarchy.service';
import {NetworkService} from '../../shared/network.service';
import {EntityType} from 'smag-commons/cfg/hierarchy';
import {can} from '../../shared/permissions';
import {isDefined} from '../../commons/commons';
import {TimeFilter, TimeFilterProfile} from '../../shared/time-filter/time-filter.component';
import {Permission} from 'smag-commons/dto/api';
import {SmagApiService} from '../../api/smag-api.service';
import {mkMemberName} from '../../shared/energy-community';
import {I18NMatPaginatorIntl, I18NMatPaginatorIntlConfig} from '../../shared/i18n-mat-paginator-intl';
import {MatPaginator, MatPaginatorIntl} from '@angular/material/paginator';
import {I18NTimestampPipe} from '../../i18n/i18n.pipe';
import {AuthService} from 'auth-commons';
import {CustomerCareService} from '../../shared/customer-care.service';

type ViewNotificationWithMeta = ViewNotification & { metaString: Observable<string> };

const DEFAULT_SEVERITIES = [
    Severity.NORMAL,
    Severity.INFO,
    Severity.ALERT
];

interface PagedNotifications {
    data: ViewNotificationWithMeta[];
    total: number;
}

const PAGE_SIZE = 10;
const PAGE_SIZES = [10, 20, 50];

@Component({
    selector: 'app-notifications',
    templateUrl: './ec-notifications.component.html',
    styleUrls: ['./ec-notifications.component.scss'],
    providers: [
        {
            provide: I18NMatPaginatorIntlConfig,
            useValue: {itemsPerPageLabel: 'Notifications per page'} as I18NMatPaginatorIntlConfig
        },
        {provide: MatPaginatorIntl, useClass: I18NMatPaginatorIntl, deps: [I18NService, I18NMatPaginatorIntlConfig]}
    ]
})
export class ECNotificationsComponent implements OnInit, OnDestroy, AfterViewInit {

    private destroyed = new Subject<void>();
    private timeFilter: ReplaySubject<TimeFilter>; // currently selected time filter
    private severities: BehaviorSubject<Severity[]>; // currently displayed severity levels
    private reloadPages: Subject<void>;
    @ViewChild('paginator') private paginator: MatPaginator;

    pagedNotifications: BehaviorSubject<PagedNotifications>;
    notifications: Observable<ViewNotificationWithMeta[]>;
    selectedNotification: ViewNotificationWithMeta; // currently expanded notification
    entity: Observable<HierarchyEntity>; // currently selected entity

    // exported in HTML
    TimeFilterProfile = TimeFilterProfile;
    DEFAULT_SEVERITIES = DEFAULT_SEVERITIES;
    Severity = Severity;
    isDefined = isDefined;

    PAGE_SIZE = PAGE_SIZE;
    PAGE_SIZES = PAGE_SIZES;
    readonly STATUSES = STATUSES;
    private timestampPipe: I18NTimestampPipe;
    private customerCare = false;

    constructor(@Inject(APP_CONFIG_TOKEN) private readonly cfg: AppConfig,
                private readonly router: Router,
                private readonly i18n: I18NService,
                private readonly hierarchyService: HierarchyService,
                private readonly networkService: NetworkService,
                private readonly notificationService: NotificationsService,
                private readonly auth: AuthService,
                private readonly customerCareService: CustomerCareService,
                private readonly apiService: SmagApiService) {
        this.timeFilter = new ReplaySubject<TimeFilter>(1);
        this.severities = new BehaviorSubject<Severity[]>(DEFAULT_SEVERITIES);
        this.notifications = null;
        this.selectedNotification = null;
        this.reloadPages = new Subject<void>();
        this.pagedNotifications = new BehaviorSubject<PagedNotifications>(null);
        this.timestampPipe = new I18NTimestampPipe(this.i18n, auth);
    }

    ngOnInit() {
        this.entity = this.hierarchyService.selection.pipe(
            map(resource => !resource || resource.type === EntityType.ENERGY_COMMUNITY ? resource : null),
            shareReplay(1),
            takeUntil(this.destroyed)
        );
        this.notifications = combineLatest([this.entity, this.timeFilter, this.severities]).pipe(
            tap(() => this.clearSelection()),
            switchMap(([entity, timeFilter, severities]) => (!!entity && this.isReadable(entity) ?
                this.notificationService.getECNotifications(entity.id, timeFilter, severities) :
                of([] as ViewNotification[])).pipe(
                map(nots => nots.map(not => ({
                    ...not,
                    metaString: this.mkNotificationMetadata(not.not)
                } as ViewNotificationWithMeta))),
                startWith(null as ViewNotificationWithMeta[]) // trigger loading spinner
            )),
            takeUntil(this.destroyed)
        );

        this.customerCareService.customerCare.pipe(takeUntil(this.destroyed))
            .subscribe(customerCare => this.customerCare = customerCare);
    }

    ngAfterViewInit() {
        merge(this.paginator.page,
            this.reloadPages.pipe(tap(() => this.paginator.firstPage()))).pipe(
            startWith(undefined as unknown),
            switchMap(() => this.notifications.pipe(filter(n => n !== null))),
            switchMap(notifications =>
                this.mkPagedNotifications(notifications, this.paginator.pageSize, this.paginator.pageIndex).pipe(
                    startWith(null as PagedNotifications) // trigger loading spinner
                )),
            takeUntil(this.destroyed)
        ).subscribe(pagedNotifications => {
            if (pagedNotifications) {
                this.pagedNotifications.next(pagedNotifications);
            }
        });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    isReadable(entity: HierarchyEntity): boolean {
        return can(entity.permissions, Permission.READ);
    }

    onClickGotoDashboard() {
        this.router.navigate(['/c', 'dev-dashboard'], {
            queryParamsHandling: 'preserve'
        }).then();
    }

    get customerCareEnabled(): Observable<boolean> {
        return this.customerCareService.customerCare;
    }

    getTomorrowDates(timestamp: number, end = false) {
        const date = new Date(timestamp);
        const nextMidnight = new Date(date);
        nextMidnight.setHours(24, 0, 0, 0);

        // (next midnight minus one millisecond
        const endOfDay = new Date(nextMidnight.getTime() + 24 * 60 * 60 * 1000 - 1);

        return !end ? this.timestampPipe.transform(nextMidnight) : this.timestampPipe.transform(endOfDay);
    }

    getWeekDates(timestamp: number, end = false) {
        const date = new Date(timestamp);
        let todayMidnight = new Date(date);
        todayMidnight.setHours(0, 0, 0, 0);
        todayMidnight = new Date((todayMidnight.getTime() - 1));

        // previous monday midnight
        const endOfWeek = new Date(todayMidnight.getTime() - (7 * 24 * 60 * 60 * 1000) + 1);

        return !end ? this.timestampPipe.transform(endOfWeek) : this.timestampPipe.transform(todayMidnight);
    }

    onClickGotoSection(status: string) {
        switch (status) {
            case STATUSES.EC_STATISTICS_FAIL:
                this.router.navigate(['/c', 'ec', 'resources'], {
                    queryParamsHandling: 'preserve'
                }).then();
                break;
            case STATUSES.EC_MISSING_CONSUMPTION_ADVICE:
                this.router.navigate(['/c', 'ec', 'weather-forecast'], {
                    queryParamsHandling: 'preserve'
                }).then();
                break;
            case STATUSES.EC_WEEKLY_PERFORMANCE_FAIL:
                if (this.customerCare) {
                    this.router.navigate(['/c', 'customer-care'], {
                        queryParamsHandling: 'preserve'
                    }).then();
                }
                break;
            case STATUSES.EC_CONSUMPTION_ADVICE_SEND_FAIL:
                if (this.customerCare) {
                    this.router.navigate(['/c', 'customer-care'], {
                        queryParamsHandling: 'preserve'
                    }).then();
                }
                break;
            case STATUSES.EC_WEEKLY_PERFORMANCE_SEND_FAIL:
                if (this.customerCare) {
                    this.router.navigate(['/c', 'customer-care'], {
                        queryParamsHandling: 'preserve'
                    }).then();
                }
                break;
            default:
                break;
        }
    }

    onSelectTimeFilter(event: TimeFilter) {
        this.timeFilter.next(event);
        this.reloadPages.next();
    }

    knownStatus(status: string) {
        switch (status) {
            case STATUSES.EC_STATISTICS_FAIL:
                return true;
            case STATUSES.EC_MISSING_CONSUMPTION_ADVICE:
                return true;
            case STATUSES.EC_WEEKLY_PERFORMANCE_FAIL:
                return true;
            case STATUSES.EC_CONSUMPTION_ADVICE_SEND_FAIL:
                return true;
            case STATUSES.EC_WEEKLY_PERFORMANCE_SEND_FAIL:
                return true;
            default:
                return false;
        }
    }

    onOpenNotificationPanel(not: ViewNotificationWithMeta) {
        this.clearSelection();
        this.selectedNotification = not;
        if (not.hasDetails) {
            setTimeout(() => {
                this.selectedNotification = not;
            }, 0);
        }
    }

    onCloseNotificationPanel() {
        this.clearSelection();
    }

    mkNotificationMetadata(not: Notification): Observable<string> {
        const memberId: string = (not.metadata || {})[NOTIFICATION_METADATA.METADATA_MEMBER_ID];
        return (memberId ?
            this.apiService.loadMember(memberId).pipe(
                map(member => `${this.i18n.translate('Member')}: ${mkMemberName(member)}`),
                catchError(() => of(null as string)),
            ) :
            of(null as string)).pipe(
            map(metaStr => {
                const pod: string = (not.metadata || {})[NOTIFICATION_METADATA.METADATA_POD];
                return pod ? `${metaStr ? metaStr + ', ' : ''}POD: ${pod}` : metaStr;
            })
        );
    }

    private mkPagedNotifications(notifications: ViewNotificationWithMeta[], size: number, page: number): Observable<PagedNotifications> {
        const pagedNotifications = [];
        for (let i = 0; i < notifications.length; i += size) {
            pagedNotifications.push(notifications.slice(i, i + size));
        }
        return of({
            data: pagedNotifications[page],
            total: notifications.length
        } as PagedNotifications);
    }

    private clearSelection() {
        this.selectedNotification = null;
    }


}
